import { useState } from "react";
import { Copy, Pause, Pencil, Play, RefreshCw, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useClipboard } from "@/lib/hooks/useClipboard";
import { cn } from "@/lib/utils";
import { inviteErrorLabel, isGroupFull, isGroupReceiving, rotatorUrl } from "@/lib/rotators";
import {
  useDeleteRotator,
  useRenewRotatorInvite,
  useSetRotatorGroupEnabled,
  useSetRotatorPaused,
  type Rotator,
  type RotatorGroup,
} from "@/lib/hooks/useRotators";

function InviteBadge({ group }: { readonly group: RotatorGroup }): JSX.Element {
  if (isGroupFull(group.size)) return <Badge variant="warning">lotado</Badge>;
  if (group.invite_status === "ready") return <Badge variant="success">convite ok</Badge>;
  if (group.invite_status === "pending") return <Badge variant="muted">gerando convite...</Badge>;
  return <Badge variant="destructive">{inviteErrorLabel(group.invite_error)}</Badge>;
}

function GroupRow({
  group,
  index,
  total,
}: {
  readonly group: RotatorGroup;
  readonly index: number;
  readonly total: number;
}): JSX.Element {
  const setEnabled = useSetRotatorGroupEnabled();
  const renew = useRenewRotatorInvite();
  const title = group.title ?? group.group_jid;
  const error = setEnabled.error ?? renew.error;
  return (
    <li
      className={cn(
        "flex flex-col gap-1 border-t border-[hsl(var(--border))] py-2 first:border-t-0",
        !group.enabled && "opacity-60",
      )}
    >
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="w-5 shrink-0 font-mono text-xs text-muted-foreground">{index + 1}.</span>
          <span className="min-w-0 flex-1 truncate text-sm text-foreground">{title}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 pl-7 sm:pl-0">
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {group.clicks.toLocaleString("pt-BR")} entradas (
            {total > 0 ? Math.round((group.clicks / total) * 100) : 0}%)
          </span>
          <InviteBadge group={group} />
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Gerar novo convite para ${title}`}
            onClick={() => renew.mutate(group.id)}
            disabled={renew.isPending || group.invite_status === "pending"}
          >
            <RefreshCw size={14} />
          </Button>
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input
              type="checkbox"
              role="switch"
              aria-label={`Grupo ${title} ligado`}
              checked={group.enabled}
              onChange={(e) => setEnabled.mutate({ groupId: group.id, enabled: e.target.checked })}
              className="h-4 w-4 accent-[hsl(var(--primary))]"
            />
            {group.enabled ? "ligado" : "desligado"}
          </label>
        </div>
      </div>
      {error && (
        <p role="alert" className="pl-7 text-xs text-[hsl(var(--destructive))]">
          {error.message}
        </p>
      )}
    </li>
  );
}

export function RotatorCard({
  rotator,
  onEdit,
}: {
  readonly rotator: Rotator;
  readonly onEdit: () => void;
}): JSX.Element {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { copy, copiedKey, failed } = useClipboard();
  const setPaused = useSetRotatorPaused();
  const remove = useDeleteRotator();
  const url = rotatorUrl(rotator.code);
  const paused = rotator.status === "paused";
  const total = rotator.group_rotator_groups.reduce((sum, g) => sum + g.clicks, 0);
  const active = rotator.group_rotator_groups.filter(isGroupReceiving).length;
  const error = setPaused.error ?? remove.error;

  return (
    <Card variant="raised" className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-base font-semibold text-foreground">{rotator.name}</h3>
            <Badge variant={paused ? "warning" : "success"}>{paused ? "pausado" : "ativo"}</Badge>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {total.toLocaleString("pt-BR")} entradas · {active} de{" "}
            {rotator.group_rotator_groups.length} grupos recebendo
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPaused.mutate({ id: rotator.id, paused: !paused })}
            disabled={setPaused.isPending}
          >
            {paused ? <Play size={14} /> : <Pause size={14} />} {paused ? "Retomar" : "Pausar"}
          </Button>
          <Button variant="secondary" size="sm" onClick={onEdit}>
            <Pencil size={14} /> Editar
          </Button>
          {confirmDelete ? (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => remove.mutate(rotator.id)}
              disabled={remove.isPending}
            >
              Confirmar exclusão
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Excluir ${rotator.name}`}
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 size={14} />
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface))] px-3 py-2">
        <code className="min-w-0 flex-1 truncate text-sm text-foreground">{url}</code>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void copy(url, rotator.id)}
          aria-label="Copiar link do rodízio"
        >
          <Copy size={14} /> {copiedKey === rotator.id ? "Copiado" : "Copiar"}
        </Button>
      </div>
      {failed && (
        <p role="alert" className="text-xs text-[hsl(var(--destructive))]">
          Não foi possível copiar. Selecione o link e copie manualmente.
        </p>
      )}
      {paused && (
        <p className="text-xs text-[hsl(var(--gold))]">
          Pausado: quem abrir o link vê um aviso de grupo indisponível.
        </p>
      )}
      <ol aria-label={`Grupos de ${rotator.name}`}>
        {rotator.group_rotator_groups.map((g, i) => (
          <GroupRow key={g.id} group={g} index={i} total={total} />
        ))}
      </ol>
      {error && (
        <p role="alert" className="text-xs text-[hsl(var(--destructive))]">
          {error.message}
        </p>
      )}
    </Card>
  );
}
